import { differenceInDays, parse } from 'date-fns';
import {
  CrewMemberDto,
  LatLongDto,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { FlightLogEntity } from 'src/store/flight-log/flight-log.model';
import { tryParseDate } from 'src/util/dateLib';
import { uuid } from 'src/util/uuid';

export enum EntityType {
  PRDPilotRecord = 'PRDPilotRecord',
  PRDEmploymentRecord = 'PRDEmploymentRecord',
  PRDAssignmentToDutyRecord = 'PRDAssignmentToDutyRecord',
  PRDTrainingRecord = 'PRDTrainingRecord',
  PRDFinalDisciplinaryAction = 'PRDFinalDisciplinaryAction',
  PRDNDRRecord = 'PRDNDRRecord',
  PRDDrugAndAlcoholRecord = 'PRDDrugAndAlcoholRecord',
}

export enum LogMode {
  Unknown = 'Unknown',
  EntryPerFlight = 'EntryPerFlight',
  EntryPerLeg = 'EntryPerLeg',
  Simple = 'Simple',
}

export enum LogStatus {
  Unknown = 'Unknown',
  NotStarted = 'NotStarted',
  Draft = 'Draft',
  ReOpened = 'ReOpened',
  Complete = 'Complete',
}

export const DefaultLogMode: LogMode = LogMode.Simple;
export const LogModes: LogMode[] = [LogMode.EntryPerFlight, LogMode.EntryPerLeg, LogMode.Simple];
export const isLogMode = (value: any) => LogModes.includes(value);

export interface Sort<T extends string = string> {
  orderBy: T;
  direction: 'asc' | 'desc';
}

export function tryGetSortOrDefault<T extends string = string>(
  sort:
    | {
        orderBy?: T;
        direction?: 'desc' | 'asc' | '';
      }
    | undefined,
  defaultSort: Sort<T>,
): Sort<T> {
  const maybeSort = tryGetSort(sort);
  return maybeSort != null ? maybeSort : defaultSort;
}

export function tryParseDateOrDefault(date: string | null | undefined, defaultDate: Date): Date {
  const parsedDate = tryParseDate(date);

  if (parsedDate == null) {
    return defaultDate;
  }

  return parsedDate;
}

export function tryGetSort<T extends string = string>(
  sort:
    | {
        orderBy?: T;
        direction?: 'desc' | 'asc' | '';
      }
    | undefined,
): Sort<T> | undefined {
  if (sort != null && sort.direction != null && sort.direction !== '' && sort.orderBy != null) {
    return {
      orderBy: sort.orderBy,
      direction: sort.direction,
    };
  }
  return undefined;
}

export type FlightLogSummarySortableField =
  | 'dateOfService'
  | 'flightTagNumber'
  | 'flightSummaryId'
  | 'flightOutcome'
  | 'flightStatus'
  | 'flightTime'
  | 'flightType'
  | 'status'
  | 'tailNumber'
  | 'aircraftMake'
  | 'aircraftModel'
  | 'aircraftType';
export interface FlightLogQueryParameters {
  employeeId?: string;
  dispatchId?: string;
  tailNumber?: string;
  startTime?: Date;
  endTime?: Date;
  openOnly?: boolean;
  currentBaseId?: string;
  currentAircraftId?: string;
  flightTagNumber?: string;
  companyId?: string;
  deletedOnly?: boolean;
  sort?: Sort<FlightLogSummarySortableField>;
  limit?: number;
  skip?: number;
}

export interface FlightLogEntryDto {
  flightTime: Date | null;
  entryDetail: LogEntryDetailDto;
  pilotLogEntries: PilotLogEntryDto[];
}

export interface LogEntryDetailDto {
  attributes: LogEntryDetailAttributesDto;
  cabinTemperature: number | null;
  maxAltitude: number | null;
  fromWaypointId: string | null;
  toWaypointId: string | null;
  isPatientOnboard: boolean;
  billingSummary: BillingSummaryDto;
  crewOnboard: CrewOnboardDto[];
  fuelEvents: FuelEventDto[];
  passengerCount: number | null;
  totalLoadedWeightInKg: number | null;
  actIndex: number | null;
}

export interface LogEntryDetailAttributesDto {
  nvg: boolean;
  refueled: boolean;
  hasExtraMileage: boolean;
}

export interface CrewOnboardDto {
  flightCrewId: string;
  isNvg: boolean;
  hnvgo: number | null;
}

export type FuelSourceType = 'Unknown' | 'AmghBulk' | 'Fbo';

export interface FuelEventDto {
  id: string;
  totalGallons: number;
  fboId: string | null;
  fuelTankId: string | null;
  fuelSourceType: FuelSourceType;
  paymentType: PaymentType;
}

export type FlightAdvantage = 'IFR' | 'GPS';
export const flightAdvantages: FlightAdvantage[] = ['IFR', 'GPS'];

export interface BillingSummaryDto {
  loadedLegCount: number;
  totalLegCount: number;
  loadedMilesCalculated: number;
  loadedMilesActual: number;
  comment: string | null;
}

export interface MaintenanceSummaryDto {
  startingHobbsReading: number;
  endingHobbsReading: number;
  totalFlightTime: number;
  totalLandings: number;
  runOnLandings: number;
  airframeTime: number;
  engine1: EngineMaintenanceDetailDto;
  engine2: EngineMaintenanceDetailDto;
  hook: ComponentDto;
  hoist: ComponentDto;
}

export interface EngineMaintenanceDetailDto {
  time: number | null;
  starts: number;
  cycles: { [id: string]: number };
}

export interface ComponentDto {
  flightTime: number | null;
  nightTime: number | null;
  cycles: number | null;
}

export interface WaypointDto {
  id: string;
  description: string | null;
  coordinate: LatLongDto;
  arrivalTime: Date | null;
  departureTime: Date | null;
  airportId: string | null;
}

export interface FlightLogFindMyFlightDto {
  id: string;
  companyId?: string;
  flightSummaryId?: string;
  flightTagNumber?: string;
  status: LogStatus;
  flightTime?: Date;
  updatedAt?: string;
  tailNumber?: string;
}

export type DepartureType =
  | 'VFR'
  | 'IFR_Standard_DP'
  | 'IFR_ODP'
  | 'IFR_Special_Use_DP'
  | 'Diverse';

export type ApproachType =
  | 'VFR'
  | 'IFR_VOR'
  | 'IFR_SDF'
  | 'IFR_LOC'
  | 'IFR_Practice'
  | 'IFR_ILS'
  | 'IFR_GPS'
  | 'IFR_Special_Use'
  | 'IFR_Visual'
  | 'IFR_Hospital_GPS';

export type PaymentType =
  | 'Unknown'
  | 'AVFuel'
  | 'Chevron'
  | 'ConocoPhillips'
  | 'DirectBill'
  | 'EPIC'
  | 'MultiService'
  | 'PersonalCreditCard'
  | 'Shell'
  | 'Visa'
  | 'Alliance'
  | 'Colt'
  | 'Mastercard'
  | 'Titan';

export const paymentTypes: PaymentType[] = [
  'AVFuel',
  'Chevron',
  'ConocoPhillips',
  'DirectBill',
  'EPIC',
  'MultiService',
  'PersonalCreditCard',
  'Shell',
  'Visa',
  'Alliance',
  'Colt',
  'Mastercard',
  'Titan',
];

export const departureTypes: DepartureType[] = [
  'VFR',
  'IFR_Standard_DP',
  'IFR_ODP',
  'IFR_Special_Use_DP',
  'Diverse',
];

export const approachTypes: ApproachType[] = [
  'VFR',
  'IFR_VOR',
  'IFR_SDF',
  'IFR_LOC',
  'IFR_Practice',
  'IFR_ILS',
  'IFR_GPS',
  'IFR_Special_Use',
  'IFR_Visual',
  'IFR_Hospital_GPS',
];

export interface FlightHoursDto {
  day: number;
  nightUnaided: number;
  nightAided: number;
  blockTime: number;
  part91: number;
  part135: number;
  part133: number;
  ifrImc: number;
  ifrSim: number;
  part133HoistDayHours: number;
  part133HookDayHours: number;
  part133HoistNightHours: number;
  part133HookNightHours: number;
}

export interface PilotLandingsDto {
  day: number;
  night: number;
  runOn: number;
}

export interface PilotLogEntryDto {
  flightCrewId: string;
  verifiedAt: number | null;
  verifiedById: string | null;
  departures: DepartureType[];
  approaches: ApproachType[];
  hnvgo?: number;
  landings: PilotLandingsDto;
  flightHours: FlightHoursDto;
  blockStartTime: Date | null;
  blockEndTime: Date | null;
}

export type MissionOutcome = 'Unknown' | 'Underway' | 'Completed' | 'Aborted';

export function createBillingSummary(): BillingSummaryDto {
  return {
    comment: null,
    loadedLegCount: 0,
    loadedMilesActual: 0,
    loadedMilesCalculated: 0,
    totalLegCount: 0,
  };
}

export function createEngineDetail(): EngineMaintenanceDetailDto {
  return { time: 0, starts: 0, cycles: {} };
}

export function createMaintenanceSummary(): MaintenanceSummaryDto {
  return {
    engine1: createEngineDetail(),
    engine2: createEngineDetail(),
    runOnLandings: 0,
    totalFlightTime: 0,
    startingHobbsReading: 0,
    endingHobbsReading: 0,
    totalLandings: 0,
    airframeTime: 0,
    hook: {
      cycles: 0,
      flightTime: 0,
      nightTime: 0,
    },
    hoist: {
      cycles: 0,
      flightTime: 0,
      nightTime: 0,
    },
  };
}

export function createFlightLog(mode: LogMode = DefaultLogMode): FlightLogEntity {
  const isSimple = mode === 'Simple';
  return {
    advantages: [],
    entries: [],
    flightCrew: [],
    id: uuid(),
    maintenanceSummary: isSimple ? undefined : createMaintenanceSummary(),
    mode,
    status: 'Draft',
    waypoints: [],
    metadata: {},
  } as FlightLogEntity;
}

export const createWaypoint: () => WaypointDto = () => {
  return {
    airportId: null,
    arrivalTime: null,
    coordinate: { latitude: null, longitude: null } as LatLongDto,
    departureTime: null,
    description: null,
    id: uuid(),
  } as WaypointDto;
};

export function createLogEntry(
  fromWaypointId: string | null,
  toWaypointId: string | null,
): FlightLogEntryDto {
  const logEntry = {
    flightTime: null,
    entryDetail: {
      attributes: {
        hasExtraMileage: false,
        nvg: false,
        refueled: false,
      } as LogEntryDetailAttributesDto,
      fromWaypointId,
      toWaypointId,
      maxAltitude: null,
      cabinTemperature: null,
      isPatientOnboard: false,
      crewOnboard: [],
      fuelEvents: [],
      billingSummary: createBillingSummary(),
      passengerCount: null,
      totalLoadedWeightInKg: null,
      actIndex: null,
    } as LogEntryDetailDto,
    pilotLogEntries: [],
  } as FlightLogEntryDto;

  return logEntry;
}

export function createFuelEvent(): FuelEventDto {
  return {
    id: uuid(),
    fboId: null,
    fuelSourceType: 'AmghBulk',
    fuelTankId: null,
    totalGallons: 0,
    paymentType: 'Unknown',
  };
}

export function createPilotLogEntries(flightLog?: FlightLogEntity) {
  const pilotLogEntries =
    flightLog != null && flightLog.flightCrew != null
      ? flightLog.flightCrew
          .filter((m) => m.role === 'PilotInCommand')
          .map((m) => createPilotLogEntry(m.id))
      : [];
  return pilotLogEntries;
}

export function createPilotLandingsDto(): PilotLandingsDto {
  return {
    day: 0,
    night: 0,
    runOn: 0,
  };
}

export function createFlightHoursDto(): FlightHoursDto {
  return {
    day: 0,
    nightUnaided: 0,
    nightAided: 0,
    blockTime: 0,
    part91: 0,
    part135: 0,
    ifrImc: 0,
    ifrSim: 0,
    part133: 0,
    part133HoistDayHours: 0,
    part133HoistNightHours: 0,
    part133HookDayHours: 0,
    part133HookNightHours: 0,
  };
}

export function createPilotLogEntry(flightCrewId: string): PilotLogEntryDto {
  return {
    flightCrewId,
    verifiedAt: null,
    verifiedById: null,
    departures: [],
    approaches: [],
    hnvgo: 0,
    landings: { day: 0, night: 0, runOn: 0 },
    flightHours: createFlightHoursDto(),
    blockStartTime: null,
    blockEndTime: null,
  } as PilotLogEntryDto;
}

export function createFullyHydratedFlight(): FlightLogEntity {
  return {
    metadata: null,
    id: uuid(),
    companyId: 'Company-abc123',
    baseId: 'Base-abc123',
    aircraftId: 'AIRCRAFT-ABC123',
    flightSummaryId: 'Mission-abc123',
    advantages: [],
    flightCrew: [
      {
        employeeId: 'Employee-abc123',
        firstName: 'Jane',
        lastName: 'Doe',
        id: 'CrewMember-abc123',
        role: 'PilotInCommand',
      },
    ] as CrewMemberDto[],
    maintenanceSummary: {
      startingHobbsReading: 0,
      endingHobbsReading: 0,
      totalFlightTime: 0,
      totalLandings: 0,
      runOnLandings: 0,
      airframeTime: 0,
      engine1: { time: null, starts: 1, cycles: { b1: 1, n1: 2 } },
      engine2: { time: null, starts: 2, cycles: { b1: 3, n1: 4, xy: 3435 } },
      hook: {
        cycles: 0,
        flightTime: 0,
        nightTime: 0,
      },
      hoist: {
        cycles: 0,
        flightTime: 0,
        nightTime: 0,
      },
    } as MaintenanceSummaryDto,

    mode: 'EntryPerLeg',
    status: 'Draft',
    waypoints: [
      {
        airportId: 'Airport-abc123',
        arrivalTime: null,
        coordinate: { latitude: 30, longitude: 50 },
        departureTime: null,
        description: 'Waypoint #1',
        id: 'Waypoint-1',
      } as WaypointDto,
      {
        airportId: 'Airport-foobar',
        arrivalTime: null,
        coordinate: { latitude: 100, longitude: 200 },
        departureTime: null,
        description: 'Waypoint #2',
        id: 'Waypoint-2',
      } as WaypointDto,
    ] as WaypointDto[],
    entries: [
      {
        flightTime: parse('2016-08-23T15:57:57Z'),
        entryDetail: {
          maxAltitude: null,
          cabinTemperature: null,
          toWaypointId: 'Waypoint-2',
          fromWaypointId: 'Waypoint-1',
          attributes: {
            hasExtraMileage: false,
            nvg: false,
            refueled: false,
          } as LogEntryDetailAttributesDto,
          isPatientOnboard: false,
          crewOnboard: [
            { flightCrewId: 'CrewMember-abc123', isNvg: false, hnvgo: 0 },
          ] as CrewOnboardDto[],
          fuelEvents: [],
          billingSummary: createBillingSummary(),
          passengerCount: null,
          totalLoadedWeightInKg: null,
          actIndex: null,
        } as LogEntryDetailDto,
        pilotLogEntries: [
          {
            flightCrewId: 'CrewMember-abc123',
            approaches: [],
            departures: [],
            verifiedAt: null,
            verifiedById: null,
            hnvgo: 2,
            flightHours: {
              day: 0,
              ifrImc: 1,
              ifrSim: 2,
              blockTime: 3,
              nightAided: 4,
              nightUnaided: 5,
              part135: 6,
              part91: 33,
              part133HoistDayHours: 34,
              part133HoistNightHours: 35,
              part133HookDayHours: 36,
              part133HookNightHours: 37,
              part133: 0,
            },
            landings: { day: 1, night: 3, runOn: 4 },
            blockStartTime: null,
            blockEndTime: null,
          } as PilotLogEntryDto,
        ] as PilotLogEntryDto[],
      } as FlightLogEntryDto,
    ] as FlightLogEntryDto[],
  } as FlightLogEntity;
}

export function isValidDateRange(
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
  maxNumberOfDays: number,
): boolean {
  if (startDate == null || endDate == null) {
    return false;
  }
  return differenceInDays(endDate, startDate) <= maxNumberOfDays;
}
